import { FormatToSentenceCase } from 'core/utils/parsers';
import { useEffect, useMemo, useState } from 'react';

import { ExpandLess, ExpandMore, Save as SaveIcon } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { MultiSelector, Selector, isEmptyOrUndefined } from '@zippeditoolsjs/blocks';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import { contactLevelColor } from '../../utils/colors';

/**
 * Step 1: Link an existing CWS/Bruno user to the contact.
 * This step is optional. It displays the user's CWS products if a user is selected.
 */
const Step1Component = ({ t, linkedUser, setLinkedUser, cwsBrunoUsers, isLoadingCWSBrunoUsers }) => {
  const [openDashboards, setOpenDashboards] = useState(true);
  const hasLinkedUser = !isEmptyOrUndefined(linkedUser?.cws_email, 'id');

  return (
    <Grid container spacing={2}>
      {/* Link existing user */}
      <Grid item xs={12}>
        <Typography variant="h6" mb={1}>{t('cws_app.contacts.form.link_user_title', 'Link Existing User')} ({t('cws_app.contacts.form.optional', 'Optional')})</Typography>
        <Selector
          loading={isLoadingCWSBrunoUsers}
          variant='outlined'
          disableClearable={false}
          label={t('cws_app.contacts.form.user', 'User')}
          options={cwsBrunoUsers}
          name={'email'}
          value={linkedUser || null}
          setValue={(newValue) => setLinkedUser(newValue)}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" mb={1}>{t('cws_app.contacts.form.Clients_website_products', "Clients Website Products")}</Typography>
        {!hasLinkedUser ? (
          <Alert severity='info'>{t('cws_app.contacts.form.select_cws_user_products', 'Select a Clients Website user to show their products.')}</Alert>
        ) : (
          <List component="nav" sx={{ border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1, maxHeight: 240, overflow: 'auto' }}>
            {linkedUser?.cws_user_clients_products?.map(product => {
              // For the dashboard product use a collapsable list
              if (product.product_name === 'dashboards' && product.dashboards) {
                return (
                  <div key={product.product_id}>
                    {/* Checkbox button */}
                    <ListItemButton sx={{ pl: 4 }} onClick={() => setOpenDashboards(!openDashboards)}>
                      <ListItemIcon>
                        <Checkbox edge="start" checked={product.is_active} disabled />
                      </ListItemIcon>
                      <ListItemText primary={t(`cws_app.general.${product.product_name}`, product.product_name)} />
                      {openDashboards ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    {/* Collapsable list */}
                    <Collapse in={openDashboards}>
                      <List component="div" disablePadding>
                        {product.dashboards.map(dashboard => (
                          <ListItemButton key={dashboard.dashboard_id} sx={{ pl: 8, display: openDashboards ? 'flex' : 'none' }}>
                            <ListItemIcon>
                              <Checkbox edge="start" checked={dashboard.is_active} disabled />
                            </ListItemIcon>
                            <ListItemText primary={dashboard.dashboard_name} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </div>
                );
              }
              // Rest of the products
              return (
                <ListItemButton key={product.product_id} sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={product.is_active}
                      tabIndex={-1}
                      disableRipple
                      disabled
                    />
                  </ListItemIcon>
                  <ListItemText primary={t(`cws_app.general.${product.product_name}`, product.product_name)} />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Grid>
    </Grid>
  );
};

/**
 * Step 2: Manage Task Manager (Bruno) profiles for the linked user.
 * This step is optional and only active if a user with a Task Manager profile is linked in Step 1.
 */
const Step2Component = (props) => {
  const {
    t,
    selectedClient,
    linkedUser,
    profiles,
    setProfiles,
    suppliers,
    isLoadingSuppliers,
    userRoles,
    isLoadingUserRoles,
    chains,
    isLoadingChains,
    userStores,
    isLoadingUserStores,
    getStoreCategories,
    categories,
    isLoadingCategories,
    setDeletedProfileIDs
  } = props;
  const [newProfile, setNewProfile] = useState({ supplier: '', role: '', chain: '', stores: [], categories: [] });
  const filteredStores = useMemo(() => userStores.filter(store => store.chain_country_id === newProfile.chain?.chain_country_id), [userStores, newProfile.chain]);
  const categoriesParsed = useMemo(() => categories.filter(category => category.value !== null).map(category => ({ ...category, parsed_name: `(${category.value}) ${category.category_name}` })), [categories]);

  // Get the categories only from the first store in the list
  useEffect(() => {
    if (!isEmptyOrUndefined(newProfile?.stores, 'array') && newProfile?.stores[0]?.store_id !== null && selectedClient?.client_id) {
      getStoreCategories(newProfile?.stores[0].store_id, selectedClient.client_id);
    }
  }, [newProfile?.stores, selectedClient?.client_id, getStoreCategories]);

  // Methods

  const handleDeleteProfile = (id) => {
    // If the profile has a profile_id, it's an existing one from the DB, so we track its deletion.
    const profileToDelete = profiles.find(p => p.profile_id === id && typeof p.profile_id === 'number');
    if (profileToDelete) {
      setDeletedProfileIDs(prev => [...prev, id]);
    }

    const updatedProfiles = profiles.filter(p => p.profile_id !== id);
    setProfiles(updatedProfiles);
  };

  const handleAddProfile = () => {
    if (!newProfile.role || !newProfile.chain) return;

    const baseProfile = {
      supplier_id: newProfile.supplier?.id || null,
      supplier_name: newProfile.supplier?.name || '',
      role_id: newProfile.role?.role_id,
      role_name: newProfile.role?.role_name,
      chain: newProfile.chain?.name,
    };

    const newProfilesToAdd = [];

    if (newProfile.stores.length > 0) {
      // Create a profile for each selected store
      newProfile.stores.forEach(store => {
        newProfilesToAdd.push({
          ...baseProfile,
          profile_id: `new-${Date.now()}-${store.store_code}`,
          store: store.store_code,
          main_category: newProfile.categories?.map(c => c.category_name).join(', ') || '',
          stores: [{ ...store, categories: newProfile.categories }],
        });
      });
    } else {
      // Create a single profile for the entire chain
      newProfilesToAdd.push({
        ...baseProfile,
        profile_id: `new-${Date.now()}`,
        store: '',
        main_category: '',
        stores: [],
      });
    }

    setProfiles(prev => [...prev, ...newProfilesToAdd]);
    // Reset all selectors to their initial state
    setNewProfile({ supplier: null, role: null, chain: null, stores: [], categories: [] });
  };

  const handleNewProfileChange = (field, value) => {
    setNewProfile(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <Typography variant="h6" sx={{ mb: 2 }}>{t('cws_app.contacts.form.task_manager_profiles', 'Task Manager Profiles')}</Typography>
      {isEmptyOrUndefined(linkedUser?.task_manager_email, 'id') ?
        <Alert severity='info'> {t('cws_app.contacts.form.select_bruno_user_profiles', 'Select a Task Manager (Bruno) user to show their profiles.')}</Alert>
        :
        <TableContainer component={Paper} sx={{ maxHeight: 350, overflow: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('cws_app.general.Supplier', 'Supplier')}</TableCell>
                <TableCell>{t('cws_app.staff.Rol', 'Role')}</TableCell>
                <TableCell>{t('cws_app.general.chain', 'Chain')}</TableCell>
                <TableCell>{t('cws_app.general.Store', 'Store')}</TableCell>
                <TableCell>{t('cws_app.general.category', 'Category')}</TableCell>
                <TableCell>{t('cws_app.general.action', 'Action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profiles?.map((profile) => (
                <TableRow key={profile.profile_id}>
                  <TableCell>{profile.supplier_id ? `(${profile.supplier_id})` : ''} {profile.supplier_name}</TableCell>
                  <TableCell>({profile.role_id}) {FormatToSentenceCase(profile.role_name)}</TableCell>
                  <TableCell>{profile.chain}</TableCell>
                  <TableCell>{profile.store}</TableCell>
                  <TableCell>{profile.main_category}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDeleteProfile(profile.profile_id)} color="error">
                      <IconComponent iconName="trash" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {/* Add new profile row */}
              <TableRow>
                {/* Supplier selector */}
                <TableCell>
                  <Selector
                    variant='outlined'
                    loading={isLoadingSuppliers}
                    disableClearable={false}
                    label={t('cws_app.general.select_supplier', 'Supplier')}
                    options={suppliers}
                    name={'name'}
                    value={newProfile.supplier ? newProfile.supplier : null}
                    setValue={(newValue) => handleNewProfileChange('supplier', newValue || '')}
                  />
                </TableCell>
                {/* Role selector */}
                <TableCell>
                  <Selector
                    variant='outlined'
                    loading={isLoadingUserRoles}
                    disableClearable={false}
                    label={t('cws_app.staff.Rol', 'Role') + "*"} // TODO: remove * when the Selector accepts the "required" prop
                    options={userRoles.map(role => ({ ...role, name: FormatToSentenceCase(role.role_name) }))}
                    name={'name'}
                    value={newProfile.role ? newProfile.role : null}
                    setValue={(newValue) => handleNewProfileChange('role', newValue || '')}
                  />
                </TableCell>
                {/* Chain selector */}
                <TableCell>
                  <Selector
                    variant='outlined'
                    loading={isLoadingChains}
                    disableClearable={false}
                    label={t('cws_app.general.chain', 'Chain') + "*"} // TODO: remove * when the Selector accepts the "required" prop
                    options={chains}
                    name={'name'}
                    value={newProfile.chain ? newProfile.chain : null}
                    setValue={(newValue) => handleNewProfileChange('chain', newValue || '')}
                  />
                </TableCell>
                {/* Store selector */}
                <TableCell>
                  <MultiSelector
                    textFieldVariant='outlined'
                    loading={isLoadingUserStores}
                    disableClearable={false}
                    label={t('cws_app.general.Store', 'Store')}
                    options={filteredStores}
                    name={'parsed_name'}
                    tagName={'store_code'}
                    value={newProfile.stores || []}
                    setValue={(newValue) => handleNewProfileChange('stores', newValue || '')}
                  />
                </TableCell>
                {/* Category selector */}
                <TableCell>
                  <MultiSelector
                    textFieldVariant='outlined'
                    loading={isLoadingCategories}
                    disableClearable={false}
                    label={t('cws_app.general.category', 'Category')}
                    options={categoriesParsed}
                    name={'parsed_name'}
                    value={newProfile.categories || []}
                    setValue={(newValue) => handleNewProfileChange('categories', newValue || '')}
                  />
                </TableCell>
                {/* Add button */}
                <TableCell>
                  <IconButton onClick={handleAddProfile} color="success" disabled={!newProfile.role || !newProfile.chain}>
                    <IconComponent iconName="add-circle" />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      }
    </>
  );
};

/**
 * Step 3: Set the contact's own information, notification features, and organizational hierarchy.
 * This is the primary information for the contact entity itself.
 */
const Step3Component = (props) => {
  const {
    t,
    theme,
    linkedUser,
    contactForm,
    setContactForm,
    contactRoles,
    isLoadingContactRoles,
    clients,
    regions,
    isLoadingRegions,
    notificationFeatures,
    isLoadingNotificationFeatures,
    userStores,
    isLoadingUserStores
  } = props;

  // Determine which list of clients to show. If a user is linked and has a specific list of clients, use that. Otherwise, use the general list.
  const clientOptions = !isEmptyOrUndefined(linkedUser?.clients, 'array') ? linkedUser.clients : clients;
  const clientHelperText = !isEmptyOrUndefined(linkedUser?.clients, 'array') ? t('cws_app.contacts.form.client_helper', 'Showing only clients assigned to the linked user.') : '';

  const memoizedContactRoles = useMemo(() => {
    return contactRoles.map(r => ({ name: r, translated: t(`cws_app.contacts.${r}`, r) }));
  }, [contactRoles, t]);

  const [level, setLevel] = useState('-');

  /**
   * Handles changes in the organization hierarchy selectors (client, region, store).
   * It updates the form state and determines the contact's hierarchy level for display.
   */
  const handleOrgHierarchyChange = (field, value) => {
    const newState = { ...contactForm, [field]: value };
    setContactForm(newState);

    if (newState.store) {
      setLevel('store');
    } else if (newState.region) {
      setLevel('region');
    } else if (newState.client) {
      setLevel('client');
    } else {
      setLevel('-');
    }
  };

  // Automatically select the client if there is only one option available.
  useEffect(() => {
    if (clientOptions?.length === 1) {
      handleOrgHierarchyChange('client', clientOptions[0]);
    }
  }, [clientOptions]);

  return (
    <Grid container spacing={2}>
      {/* Contact Information */}
      <Grid item xs={12}>
        <Typography variant="h6">{t('cws_app.contacts.form.contact_info_title', 'Contact Information')}</Typography>
      </Grid>
      {/* Contact Name */}
      <Grid item xs={12} md={4}>
        <TextField
          variant='outlined'
          fullWidth
          label={t('cws_app.contacts.form.contact_name', 'Contact Name')}
          value={contactForm.contact_name || ''}
          onChange={(e) => setContactForm(prev => ({ ...prev, contact_name: e.target.value }))}
        />
      </Grid>
      {/* Email */}
      <Grid item xs={12} md={4}>
        <TextField
          disabled={!isEmptyOrUndefined(linkedUser, 'object')}
          variant='outlined'
          fullWidth
          label={t('cws_app.general.email', 'Email')}
          value={contactForm.email || ''}
          onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
          helperText={isEmptyOrUndefined(linkedUser, 'object') ? '' : t('cws_app.contacts.form.email_helper', 'Must match the email of the linked user.')}
        />
      </Grid>
      {/* Phone */}
      <Grid item xs={12} md={4}>
        <TextField
          variant='outlined'
          fullWidth
          label={t('cws_app.contacts.Phone', 'Phone')}
          value={contactForm.phone || ''}
          onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
          helperText={t('cws_app.contacts.form.phone_helper', 'Example: +1234567890')}
        />
      </Grid>
      {/* Contact Role */}
      <Grid item xs={12} md={4}>
        <Selector
          variant='outlined'
          disableClearable={false}
          loading={isLoadingContactRoles}
          label={t('cws_app.contacts.form.contact_role', 'Contact Role')}
          options={memoizedContactRoles}
          name={'translated'}
          value={contactForm.contact_role ? contactForm.contact_role : null}
          setValue={(newValue) => setContactForm(prev => ({ ...prev, contact_role: newValue || '' }))}
        />
      </Grid>
      {/* Notification Features */}
      <Grid item xs={12}>
        <Typography variant="h6">{t('cws_app.contacts.Notification_features', 'Notification Features')}</Typography>
      </Grid>
      <Grid item xs={12}>
        <MultiSelector
          textFieldVariant='outlined'
          loading={isLoadingNotificationFeatures}
          label={t('cws_app.contacts.Notification_features', 'Notification Features')}
          options={notificationFeatures.map(f => ({ ...f, translated: t(`cws_app.contacts.${f.name}`, f.name) }))}
          name={'translated'}
          value={contactForm.notification_features || []}
          setValue={(newValue) => setContactForm(prev => ({ ...prev, notification_features: newValue }))}
        />
      </Grid>
      {/* Organization Hierarchy */}
      <Grid item xs={12}>
        <Typography variant="h6">{t('cws_app.contacts.form.org_hierarchy_title', 'Organization Hierarchy')}</Typography>
      </Grid>
      {/* Client */}
      <Grid item xs={12} md={4}>
        <Selector
          variant='outlined'
          disableClearable={false}
          disabled={clientOptions?.length === 1}
          label={t('cws_app.contacts.client', 'Client')}
          options={clientOptions}
          name={'name'}
          value={contactForm.client ? contactForm.client : null}
          setValue={(newValue) => handleOrgHierarchyChange('client', newValue || '')}
          helperText={clientHelperText}
        />
      </Grid>
      {/* Region */}
      <Grid item xs={12} md={4}>
        <Selector
          variant='outlined'
          disableClearable={false}
          loading={isLoadingRegions}
          label={t('cws_app.contacts.region', 'Region')}
          options={regions}
          name={'name'}
          value={contactForm.region ? contactForm.region : null}
          setValue={(newValue) => handleOrgHierarchyChange('region', newValue || '')}
        />
      </Grid>
      {/* Store */}
      <Grid item xs={12} md={4}>
        <Selector
          variant='outlined'
          disableClearable={false}
          loading={isLoadingUserStores}
          label={t('cws_app.general.Store', 'Store')}
          options={userStores}
          name={'parsed_name'}
          value={contactForm.store ? contactForm.store : null}
          setValue={(newValue) => handleOrgHierarchyChange('store', newValue || '')}
        />
      </Grid>
      {/* Level */}
      <Grid item xs={12}>
        {t('cws_app.contacts.Level', 'Level')}: <Typography component="span" fontWeight="bold" sx={{ px: 1, py: 0.5, borderRadius: '5px', background: contactLevelColor(level, theme) }}>{t(`cws_app.contacts.${level}`, level)}</Typography>
      </Grid>
    </Grid>
  );
};


/**
 * The main form component, which orchestrates the multi-step process for creating or editing a contact.
 */
export default function ContactForm(props) {
  const {
    t,
    theme,
    selectedClient,
    cwsBrunoUsers,
    isLoadingCWSBrunoUsers,
    suppliers,
    isLoadingSuppliers,
    userRoles,
    isLoadingUserRoles,
    chains,
    isLoadingChains,
    userStores,
    isLoadingUserStores,
    getStoreCategories,
    categories,
    isLoadingCategories,
    postContact,
    isLoadingPostContact,
    contactRoles,
    isLoadingContactRoles,
    clients,
    regions,
    isLoadingRegions,
    notificationFeatures,
    isLoadingNotificationFeatures,
    updateBrunoUser,
    isLoadingUpdateBrunoUser,
    currentContact,
    patchContact,
    isLoadingPatchContact,
  } = props;
  const [activeStep, setActiveStep] = useState(0);
  const [contactForm, setContactForm] = useState({});
  const [linkedUser, setLinkedUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [deletedProfileIDs, setDeletedProfileIDs] = useState([]);

  /**
   * Effect to synchronize state when a user is linked in Step 1.
   * It populates the `profiles` state with the user's existing Task Manager profiles
   * and pre-fills the contact's email address in the `contactForm`.
   */
  useEffect(() => {
    setProfiles(linkedUser?.task_manager_profiles || []);
    if (linkedUser?.email) {
      setContactForm(prev => ({ ...prev, email: linkedUser.email }));
    }
  }, [linkedUser]);

  /**
   * Memoized validation logic for the "Save" button on the final step.
   * The button is disabled unless all required conditions are met:
   * 1. At least one contact method (email or phone) is provided.
   * 2. At least one notification feature is selected.
   * 3. At least one level of the organization hierarchy (client, region, or store) is selected.
   */
  const isSaveDisabled = useMemo(() => {
    const hasContactMethod = !isEmptyOrUndefined(contactForm.email, 'id') || !isEmptyOrUndefined(contactForm.phone, 'id');
    const hasNotificationFeatures = !isEmptyOrUndefined(contactForm.notification_features, 'array');
    const hasHierarchy = !isEmptyOrUndefined(contactForm.client, 'object') || !isEmptyOrUndefined(contactForm.region, 'object') || !isEmptyOrUndefined(contactForm.store, 'object');

    return !(hasContactMethod && hasNotificationFeatures && hasHierarchy);
  }, [contactForm]);

  /**
   * Effect to pre-populate the entire form when editing an existing contact.
   * It runs when `currentContact` (passed from the parent) changes.
   * It finds the full objects for linked data (user, client, region, etc.) to correctly set the state for all selectors.
   */
  useEffect(() => {
    // Reset all form states when there is no current contact to avoid carrying over old data from a previous edit.
    if (!currentContact) {
      setContactForm({});
      setLinkedUser(null);
      setProfiles([]);
      setDeletedProfileIDs([]);
      return; // Exit early if there's no contact to process.
    }
    if (currentContact) {
      // Find the full objects for selectors
      const linked = currentContact.cws_user_id
        ? (cwsBrunoUsers.find(u => u.cws_user_id === currentContact.cws_user_id) || null)
        : null;
      const client = clients.find(c => c.client_id === currentContact.client_id) || null;
      const region = regions.find(r => r.region_id === currentContact.region_id) || null;
      const store = userStores.find(s => s.store_id === currentContact.store_id) || null;
      const role = contactRoles.find(r => r === currentContact.contact_role) || null;
      const features = notificationFeatures.filter(f => currentContact.notification_features.some(cf => cf.notification_feature_id === f.notification_feature_id));

      setLinkedUser(linked);
      setProfiles(linked?.task_manager_profiles || []);
      setContactForm({
        contact_id: currentContact.contact_id,
        contact_name: currentContact.contact_name,
        email: currentContact.email,
        phone: currentContact.phone,
        client,
        region,
        store,
        contact_role: role ? { name: role, translated: t(`cws_app.contacts.${role}`, role) } : null,
        notification_features: features.map(feature => ({ ...feature, translated: t(`cws_app.contacts.${feature.name}`, feature.name) })),
      });
    }
  }, [currentContact, cwsBrunoUsers, clients, regions, userStores, contactRoles, notificationFeatures, t]);

  const steps = [
    t('cws_app.contacts.form.step1', 'Link User & CWS Products'),
    t('cws_app.contacts.form.step2', 'Task Manager Profiles'),
    t('cws_app.contacts.form.step3', 'Contact Info & Notification Features')
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  /**
   * Main save handler for the form. It orchestrates two potential API calls:
   * 1. `updateBrunoUser`: If a user was linked and their Task Manager profiles were changed, this is called first.
   * 2. `patchContact` or `postContact`: Called to save the contact's own data. It decides whether to PATCH (edit) or POST (create) based on the presence of a `contact_id`.
   */
  // TODO: only send updateBrunoUser and patchContact if the info changes
  const handleSave = () => {
    // * Task Manager (Bruno) profile changes
    // If a user is linked, update their profiles first.
    if (linkedUser?.task_manager_user_id) {
      const newProfiles = profiles.filter(p => typeof p.profile_id === 'string' && p.profile_id.startsWith('new-'));

      let patchBody = null;
      if (!isEmptyOrUndefined(newProfiles, 'array')) {
        patchBody = {
          user_id: linkedUser.task_manager_user_id,
          profiles: newProfiles.map(profile => ({ ...profile, profile_id: null })), // Remove the temporary ID, otherwise the profile is not added
        };
      }

      let deleteBody = null;
      if (!isEmptyOrUndefined(deletedProfileIDs, 'array')) {
        deleteBody = {
          user_id: linkedUser.task_manager_user_id,
          profile_ids: deletedProfileIDs,
        };
      }

      if (patchBody || deleteBody) {
        updateBrunoUser(patchBody, deleteBody);
      }
    }

    // * Contact changes
    let payload = {
      contact_id: contactForm.contact_id,
      contact_name: contactForm.contact_name || null,
      email: contactForm.email || null,
      phone: contactForm.phone || null,
      contact_role: contactForm.contact_role?.name || null,
      client_id: contactForm.client?.client_id || null,
      region_id: contactForm.region?.region_id || null,
      store_id: contactForm.store?.store_id || null,
      feature_ids: contactForm.notification_features?.map(f => f.notification_feature_id) || [],
    };

    // If editing, call patchContact, otherwise call postContact
    if (contactForm.contact_id) {
      payload = {
        ...payload,
        contact_id: contactForm.contact_id,
      };
      patchContact(payload);
    } else {
      postContact(payload);
    }
  };

  const isStepOptional = (step) => {
    return step === 0 || step === 1;
  };

  function getStepContent(step) {
    switch (step) {
      case 0:
        return (
          <Step1Component
            t={t}
            linkedUser={linkedUser}
            setLinkedUser={setLinkedUser}
            cwsBrunoUsers={cwsBrunoUsers}
            isLoadingCWSBrunoUsers={isLoadingCWSBrunoUsers}
          />
        );
      case 1:
        return (
          <Step2Component
            t={t}
            selectedClient={selectedClient}
            linkedUser={linkedUser}
            profiles={profiles}
            setProfiles={setProfiles}
            suppliers={suppliers}
            isLoadingSuppliers={isLoadingSuppliers}
            userRoles={userRoles}
            isLoadingUserRoles={isLoadingUserRoles}
            chains={chains}
            isLoadingChains={isLoadingChains}
            userStores={userStores}
            isLoadingUserStores={isLoadingUserStores}
            getStoreCategories={getStoreCategories}
            categories={categories}
            isLoadingCategories={isLoadingCategories}
            setDeletedProfileIDs={setDeletedProfileIDs}
          />
        );
      case 2:
        return (
          <Step3Component
            t={t}
            theme={theme}
            linkedUser={linkedUser}
            contactForm={contactForm}
            setContactForm={setContactForm}
            contactRoles={contactRoles}
            isLoadingContactRoles={isLoadingContactRoles}
            clients={clients}
            regions={regions}
            isLoadingRegions={isLoadingRegions}
            notificationFeatures={notificationFeatures}
            isLoadingNotificationFeatures={isLoadingNotificationFeatures}
            userStores={userStores}
            isLoadingUserStores={isLoadingUserStores}
          />
        );
      default:
        return 'Unknown step';
    }
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Dialog title */}
      <Typography variant="h5" sx={{ mb: 2 }}>{currentContact ? t('cws_app.contacts.form.edit_title', 'Edit Contact') : t('cws_app.contacts.form.title', 'Add Contact')}</Typography>
      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label, index) => {
          const stepProps = {};
          const labelProps = {};
          if (isStepOptional(index)) {
            labelProps.optional = (
              <Typography variant="caption">{t('cws_app.contacts.form.optional', 'Optional')}</Typography>
            );
          }
          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {/* Stepper content */}
      {/* Keep the container always rendered to prevent layout shift (flickering) */}
      <Box sx={{ minHeight: 400 }}>
        {getStepContent(activeStep)}
      </Box>

      {/* Stepper navigation */}
      <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
        <Button
          color="inherit"
          disabled={activeStep === 0}
          onClick={handleBack}
          sx={{ mr: 1 }}
        >
          {t('cws_app.general.back', 'Back')}
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {isStepOptional(activeStep) && (
          <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
            {t('cws_app.contacts.form.skip', 'Skip')}
          </Button>
        )}
        {activeStep === steps.length - 1 ? (
          <LoadingButton
            disabled={isSaveDisabled}
            loading={isLoadingPostContact || isLoadingPatchContact || isLoadingUpdateBrunoUser}
            variant="contained"
            loadingPosition="start"
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            {t('cws_app.general.save', 'Save')}
          </LoadingButton>
        ) : (
          <Button onClick={handleNext}>
            {t('cws_app.general.next', 'Next')}
          </Button>
        )}
      </Box>
    </Box>
  );
}